/**
 * The audit trail panel for one order card on /admin/orders.
 *
 * This is presentation only — the caller owns fetching and caching, because the
 * orders page mounts and unmounts these panels as cards are toggled and a cache
 * that lived in here would be thrown away on every collapse.
 *
 * The API already writes each `summary` for a human, so nothing here rewrites
 * prose. The job of this component is the *shape* of the trail: what happened,
 * in what order, and — the reason anyone opens it — where the time went. Hence
 * the gap chips between entries and the loud styling on the handful of kinds
 * that mean something went wrong.
 */
import {
  History, Receipt, CalendarClock, CircleDot, Timer, TimerReset,
  Bell, BellRing, BellOff, CheckCircle2, Users, ThumbsDown, UserCheck,
  Package, CheckCheck, UserMinus, KeyRound, RotateCcw, ShieldAlert,
  MessageCircle, ListChecks, Store, Truck, User, Cog, Loader2, AlertTriangle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Play } from "lucide-react";

function authHeaders(): Record<string, string> {
  const t = localStorage.getItem("ayrshire_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Plays a shop-call recording. The endpoint is admin-Bearer-auth, so a plain
 *  <audio src> can't reach it — fetch the blob with the token, then play. */
function CallRecordingPlayer({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) { setErr(r.status === 404 ? "No recording — the shop didn't answer this call" : "Couldn't load recording"); return; }
      setSrc(URL.createObjectURL(await r.blob()));
    } catch { setErr("Network error"); }
    finally { setLoading(false); }
  };
  if (src) return <audio controls src={src} className="mt-1 h-8 w-full max-w-[240px]" />;
  return (
    <div className="mt-1">
      <button
        onClick={() => void load()} disabled={loading}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white"
      >
        <Play size={11} /> {loading ? "Loading…" : "Play call recording"}
      </button>
      {err && <span className="ml-2 text-[10px] text-slate-500">{err}</span>}
    </div>
  );
}

export type TimelineEntry = {
  at: string;
  /** Machine key, so we can pick an icon without parsing the summary. */
  kind: string;
  summary: string;
  actorType: "shop" | "driver" | "customer" | "admin" | "system";
  actorUserId?: number | null;
  actor?: string | null;
  detail?: Record<string, unknown>;
};

export type OrderTimelineData = {
  orderId: number;
  storeName: string | null;
  status: string;
  placedAt: string;
  /** Order -> delivered. Null while the order is still running, or if cancelled. */
  totalMinutes: number | null;
  entries: TimelineEntry[];
};

/** Owned by the orders page so it survives the panel being collapsed. */
export type TimelineState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; data: OrderTimelineData };

/* All times are shown in Europe/London regardless of the admin's own clock —
 * shops, drivers and support all talk about orders in local time, and an admin
 * abroad reading UTC would misread every escalation window on the page. */
const LONDON_CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hour12: false,
});
const LONDON_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London", day: "numeric", month: "short",
});
/** Sortable London calendar day, used only to decide whether to print a date. */
const LONDON_DAY_KEY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
});

function durationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/**
 * The gap since the previous entry, or null when it is under a minute.
 * Back-to-back writes (a status change and the push that followed it) would
 * otherwise fill the trail with "+0 min" and bury the real waits.
 */
function gapLabel(ms: number): string | null {
  if (ms <= 60_000) return null;
  return `+${durationLabel(Math.round(ms / 60_000))}`;
}

/**
 * Kinds worth interrupting a scan for. Everything else is a routine step and
 * stays grey — if half the trail is coloured, none of it is.
 *
 *   critical  something broke and a customer probably felt it
 *   manual    a human overrode the system, so the trail is not self-explanatory
 */
type Tone = "critical" | "manual" | "done" | "quiet";

function toneFor(kind: string): Tone {
  if (kind.startsWith("admin:")) return "manual";
  switch (kind) {
    case "driver_released_after_pickup": // food was in the car and came back out
    case "alert_expired":                // the shop never saw the order
    case "delivery_code_failed":         // handover to the wrong person, or a dispute
    case "refunded":
      return "critical";
    case "delivered":
      return "done";
    default:
      return "quiet";
  }
}

const TONE_STYLES: Record<Tone, { dot: string; body: string; text: string }> = {
  critical: {
    dot: "bg-red-100 text-red-600 border-red-200",
    body: "bg-red-50 border border-red-200 px-2 py-1.5 rounded-lg",
    text: "text-red-700 font-semibold",
  },
  manual: {
    dot: "bg-indigo-100 text-indigo-600 border-indigo-200",
    body: "bg-indigo-50 border border-indigo-200 px-2 py-1.5 rounded-lg",
    text: "text-indigo-700 font-semibold",
  },
  done: {
    dot: "bg-emerald-100 text-emerald-600 border-emerald-200",
    body: "",
    text: "text-emerald-700 font-semibold",
  },
  quiet: {
    dot: "bg-white text-slate-400 border-slate-200",
    body: "",
    text: "text-slate-600",
  },
};

const KIND_ICONS: Record<string, LucideIcon> = {
  placed: Receipt,
  scheduled: CalendarClock,
  status_changed: CircleDot,
  ready_time_set: Timer,
  ready_time_extended: TimerReset,
  shop_alerted: Bell,
  shop_chased: BellRing,
  shop_acknowledged: CheckCircle2,
  alert_expired: BellOff,
  drivers_offered: Users,
  driver_declined: ThumbsDown,
  driver_assigned: UserCheck,
  picked_up: Package,
  delivered: CheckCheck,
  driver_released: UserMinus,
  driver_released_after_pickup: UserMinus,
  delivery_code_failed: KeyRound,
  refunded: RotateCcw,
  message: MessageCircle,
};

const ACTOR_ICONS: Record<TimelineEntry["actorType"], LucideIcon> = {
  shop: Store,
  driver: Truck,
  customer: User,
  admin: ShieldAlert,
  system: Cog,
};

/** Kinds are namespaced (`admin:refund`, `item:substituted`), so match prefixes too. */
function iconFor(entry: TimelineEntry): LucideIcon {
  const exact = KIND_ICONS[entry.kind];
  if (exact) return exact;
  if (entry.kind.startsWith("item:")) return ListChecks;
  if (entry.kind.startsWith("admin:")) return ShieldAlert;
  return ACTOR_ICONS[entry.actorType] ?? Cog;
}

/** Shared column widths keep the rail, the gap chips and the rows in one line. */
const TIME_COL = "w-9 sm:w-12 shrink-0";
const RAIL_COL = "w-5 shrink-0";

function PanelShell({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:p-4">
      {children}
    </div>
  );
}

export default function OrderTimeline({
  state,
  onRetry,
}: {
  state: TimelineState;
  onRetry: () => void;
}) {
  if (state.phase === "loading") {
    return (
      <PanelShell>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 size={13} className="animate-spin shrink-0" />
          Loading audit trail…
        </div>
      </PanelShell>
    );
  }

  if (state.phase === "error") {
    return (
      <PanelShell>
        <div className="flex flex-wrap items-center gap-2 text-xs text-red-700">
          <AlertTriangle size={13} className="shrink-0" />
          <span className="min-w-0 break-words">{state.message}</span>
          <button
            onClick={onRetry}
            className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-xs font-semibold text-red-700 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            Try again
          </button>
        </div>
      </PanelShell>
    );
  }

  const { data } = state;
  const orderDayKey = LONDON_DAY_KEY.format(new Date(data.placedAt));

  return (
    <PanelShell>
      {/* Header: the headline number first, because "how long did this take" is
          the question that made the admin open the panel. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
        <h4 className="font-bold text-sm flex items-center gap-1.5">
          <History size={13} className="text-slate-400" /> Audit trail
        </h4>
        {data.totalMinutes != null && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            Delivered in {durationLabel(data.totalMinutes)}
          </span>
        )}
        <span className="text-xs text-slate-400">
          {data.entries.length} {data.entries.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      {data.entries.length === 0 ? (
        <p className="text-xs text-slate-500">
          Nothing was recorded for this order. That is normal for older orders placed
          before the audit trail existed — it does not mean anything went wrong.
        </p>
      ) : (
        <ol className="space-y-0">
          {data.entries.map((entry, index) => {
            const at = new Date(entry.at);
            const previous = index > 0 ? data.entries[index - 1] : null;
            const gap = previous ? gapLabel(at.getTime() - new Date(previous.at).getTime()) : null;
            // A gap this long is usually the answer to "why was this order late",
            // so it gets to shout while the routine ones stay grey.
            const slowGap = previous
              ? at.getTime() - new Date(previous.at).getTime() >= 10 * 60_000
              : false;
            const tone = TONE_STYLES[toneFor(entry.kind)];
            const Icon = iconFor(entry);
            const isLast = index === data.entries.length - 1;
            // Multi-day orders (scheduled, or a refund days later) need the date;
            // printing it on every row of a 40-minute order is just noise.
            const showDate = LONDON_DAY_KEY.format(at) !== orderDayKey;

            return (
              <li key={`${entry.at}-${entry.kind}-${index}`}>
                {gap && (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className={TIME_COL} />
                    <span className={`${RAIL_COL} flex justify-center`}>
                      <span className="w-px h-5 bg-slate-200" />
                    </span>
                    <span
                      className={`text-[10px] font-semibold tabular-nums ${
                        slowGap ? "text-amber-600" : "text-slate-400"
                      }`}
                    >
                      {gap}
                    </span>
                  </div>
                )}

                <div className="flex gap-2 sm:gap-3">
                  <time
                    dateTime={entry.at}
                    className={`${TIME_COL} pt-1 text-[11px] font-mono tabular-nums text-slate-400 text-right leading-tight`}
                  >
                    {LONDON_CLOCK.format(at)}
                  </time>

                  {/* Rail: the icon plus the connector down to the next entry. */}
                  <div className={`${RAIL_COL} flex flex-col items-center`}>
                    <span
                      className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${tone.dot}`}
                    >
                      <Icon size={11} />
                    </span>
                    {!isLast && <span className="flex-1 w-px bg-slate-200 min-h-[8px]" />}
                  </div>

                  <div className={`min-w-0 flex-1 ${isLast ? "pb-0" : "pb-3"}`}>
                    <div className={tone.body}>
                      <p className={`text-xs leading-snug break-words ${tone.text}`}>
                        {entry.summary}
                      </p>
                      {(entry.actor || showDate) && (
                        <p className="text-[10px] text-slate-400 mt-0.5 break-words">
                          {entry.actor}
                          {entry.actor && showDate ? " · " : ""}
                          {showDate ? LONDON_DATE.format(at) : ""}
                        </p>
                      )}
                      {typeof entry.detail?.["recordingUrl"] === "string" && (
                        <CallRecordingPlayer url={entry.detail["recordingUrl"] as string} />
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </PanelShell>
  );
}
